import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Select, { SingleValue } from "react-select";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  PolarRadiusAxis,
} from "recharts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExamApi } from "@/api/examApi";
import { useAuth } from "@/contaxt/AuthContext";
import apiClient from "@/api/apiClient";
import { supabase } from "@/api/supabaseClient";
import {
  getToken,
  clearSessionId,
  clearGuestExamUser,
  clearExamId,
  clearGuestId,
  clearRetryAndExamStorage,
} from "@/utils/storage";
import "./../styles/Dashboard.css";
import watch from "../../public/images/watch.svg";
import pre from "../../public/images/workspace_premium.svg";
import tem from "../../public/images/template.svg";
import play from "../../public/images/play_circle_filled.svg";
import refresh from "../../public/images/refresh.svg";
import radar from "../../public/images/Background.svg";
import progress from "../../public/images/progress.svg";
import expendPlan from "../../public/images/expendPlan.svg";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { checkSubscriptionAccess } from "@/utils/subscriptionCheck";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const TRIAL_BANNER_KEY = "trial_banner_dismissed_v2";

interface GoalOption {
  value: string;
  label: string;
}

const goalOptions: GoalOption[] = [
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "C1", label: "C1" },
  { value: "C2", label: "C2" },
];

const Dashboard = () => {
  const [skillData, setSkillData] = useState([
    { skill: "Fluency", value: 0 },
    { skill: "Grammar", value: 0 },
    { skill: "Vocabulary", value: 0 },
    { skill: "Pronunciation", value: 0 },
    { skill: "Comprehension", value: 0 },
  ]);

  const [radarMeta, setRadarMeta] = useState<{
    focus?: string[];
    strength?: string[];
  }>({
    focus: [""],
    strength: [""],
  });

  const [motivationText, setMotivationText] = useState<string>("");

  const { getExamList, getExamListDashboard } = useExamApi();
  const { isAuthenticated, getPaidUser } = useAuth();
  const navigate = useNavigate();

  const [loadingExams, setLoadingExams] = useState(true);
  const [fetchedExams, setFetchedExams] = useState<any[]>([]);
  const [subsLoading, setSubsLoading] = useState<boolean>(true);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [latestExamWithFeedbackId, setLatestExamWithFeedbackId] = useState<
    string | null
  >(null);
  const [selectedTaskType] = useState<string>("task1");
  const [pageLoading, setPageLoading] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
  const [pendingGoal, setPendingGoal] = useState<GoalOption | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<string>("B1");
  const [progressLoading, setProgressLoading] = useState(true);
  const [previousGoal, setPreviousGoal] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Trial-related state
  const [trialExamId, setTrialExamId] = useState<string | null>(null);
  const [trialExamFeedback, setTrialExamFeedback] = useState<any | null>(null);
  const [hasSeenTrialBanner, setHasSeenTrialBanner] = useState<boolean | null>(
    null
  );

  const [progressData, setProgressData] = useState<{
    percent: number;
    message: { title: string; text: string };
    current_level: string;
  } | null>(null);
    // Add state to track if user is improving
  const [isImproving, setIsImproving] = useState<boolean>(false);

  // Clear session/exam data on mount
  useEffect(() => {
    clearSessionId();
    clearGuestExamUser();
    clearExamId();
    clearGuestId();
    clearRetryAndExamStorage();
    localStorage.removeItem("current_task_type");
    localStorage.removeItem("task2_topic_id");
    localStorage.removeItem("task3_topic_id");
  }, []);

  // Recalculate progress on dashboard mount to ensure latest data
  useEffect(() => {
    const recalculateProgressOnLoad = async () => {
      const userId = getPaidUser()?.userId;
      const goalLevel = localStorage.getItem("goal_level");
      
      if (!userId || !goalLevel) {
        console.log("Dashboard: Skipping recalc-progress on mount - missing userId or goal_level");
        return;
      }

      try {
        console.log("Dashboard: Calling recalc-progress on mount with user_id:", userId, "goal_cefr:", goalLevel);
        
        const res = await fetch(
          "https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/recalc-progress",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({ user_id: userId, goal_cefr: goalLevel }),
          }
        );

        if (!res.ok) {
          throw new Error(`Progress API error: ${res.status}`);
        }

        const data = await res.json();
        console.log("Dashboard: recalc-progress on mount returned:", data);
        
        // Refresh progress data after recalculation
        await fetchProgress();
      } catch (err) {
        console.error("Dashboard: Failed to recalculate progress on mount:", err);
      }
    };

    recalculateProgressOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Goal change handler (opens confirmation modal)
  const handleChangeGole = (option: SingleValue<GoalOption>) => {
    if (!option) return;
    setPendingGoal(option);
    setShowConfirmationModal(true);
  };

  // Load / refresh progress (using get_exam_progress)
  const fetchProgress = async () => {
    setProgressLoading(true);
    const userId = getPaidUser()?.userId;
    if (!userId) {
      setProgressLoading(false);
      return null;
    }

    try {
      const res = await fetch(
        "https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/get_exam_progress",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      if (!res.ok) {
        throw new Error(`Progress API error: ${res.status}`);
      }

      const data = await res.json();

      setProgressData({
        percent: data.progress_percent,
        message: {
          title: data.message_title,
          text: data.message_text,
        },
        current_level: data.current_level,
      });

      // Calculate if user is improving based on exam history
      if (fetchedExams.length >= 2) {
        try {
          // Get the most recent exam's overall CEFR
          const latestCefr = fetchedExams[0]?.feedback_json?.overall?.cefr_level;
          // Get an older exam's overall CEFR (from 2-3 exams ago)
          const olderExam = fetchedExams[Math.min(2, fetchedExams.length - 1)];
          const olderCefr = olderExam?.feedback_json?.overall?.cefr_level;
          
          if (latestCefr && olderCefr) {
            const cefrOrder = ["A1", "A2", "B1", "B2", "C1", "C2"];
            const latestIndex = cefrOrder.indexOf(latestCefr);
            const olderIndex = cefrOrder.indexOf(olderCefr);
            
            // User is improving if latest index is higher than older index
            setIsImproving(latestIndex > olderIndex);
          }
        } catch (err) {
          console.warn("Failed to calculate improvement trend:", err);
          setIsImproving(false);
        }
      } else {
        setIsImproving(false);
      }
      
      const storedGoal = localStorage.getItem("goal_level");
      if (!storedGoal && data.goal_cefr) {
        localStorage.setItem("goal_level", data.goal_cefr);
        setSelectedGoal({ value: data.goal_cefr, label: data.goal_cefr });
      }

      if (data.goal_cefr && !selectedGoal) {
        setSelectedGoal({ value: data.goal_cefr, label: data.goal_cefr });
      }

      return data;
    } catch (err) {
      console.error("Failed to fetch progress:", err);
      return null;
    } finally {
      setProgressLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getPaidUser, fetchedExams]);

  // Confirm goal change – uses recalc-progress
  const handleConfirmGoal = async () => {
    if (!pendingGoal) return;

    setIsConfirming(true);
    try {
      const userId = getPaidUser()?.userId;
      if (!userId) return;

      console.log(
        "Dashboard: Calling recalc-progress with user_id:",
        userId,
        "goal_cefr:",
        pendingGoal.value
      );

      const res = await fetch(
        "https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/recalc-progress",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({ user_id: userId, goal_cefr: pendingGoal.value }),
        }
      );

      if (!res.ok) {
        throw new Error(`Progress API error: ${res.status}`);
      }

      const data = await res.json();
      console.log("Dashboard: recalc-progress returned:", data);

      // Retry logic: poll for updated progress data
      let retries = 0;
      const maxRetries = 5;
      let updatedData = null;

      while (retries < maxRetries) {
        // Wait 1 second before fetching
        await new Promise((resolve) => setTimeout(resolve, 1000));
        
        updatedData = await fetchProgress();
        
        // Check if the fetched goal matches the pending goal
        if (updatedData && updatedData.goal_cefr === pendingGoal.value) {
           console.log("Dashboard: Progress updated successfully on retry", retries + 1);
           break;
        }
        
        retries++;
        console.log(`Dashboard: Retry ${retries}/${maxRetries} - waiting for goal update...`);
      }
      
      // after recalc-progress succeeds
      setSelectedGoal(pendingGoal);
      localStorage.setItem("goal_level", pendingGoal.value);

      if (
        fetchedExams.length > 0 &&
        fetchedExams[0].feedback_json?.overall?.cefr_level
      ) {
        setCurrentLevel(fetchedExams[0].feedback_json.overall.cefr_level);
      }
    } catch (err) {
      console.error("Dashboard: Failed to recalculate progress:", err);
    } finally {
      setShowConfirmationModal(false);
      setIsConfirming(false);
    }
  };

  const handleCancelGoal = () => {
    setPendingGoal(null);
    setShowConfirmationModal(false);
  };

   // ✅ Mark the trial banner as seen once per device + per account (if possible)
  const markTrialBannerSeen = async () => {
    try {
      // 🔹 Local fallback: once dismissed on this browser, never show again
      localStorage.setItem(TRIAL_BANNER_KEY, "true");
  
      // Optimistic UI: hide banner immediately
      setHasSeenTrialBanner(true);
  
      // 🔹 Account-level flag in Supabase (best effort)
      const paidUser = getPaidUser();
      if (!paidUser?.userId) return;
  
      const { error } = await supabase
        .from("users")
        .update({ has_seen_trial_banner: true })
        .eq("id", paidUser.userId);
  
      if (error) {
        console.error("Dashboard: failed to update has_seen_trial_banner", error);
      }
    } catch (err) {
      console.error("Dashboard: error updating banner flag", err);
    }
  };


  // Open the free trial result on the detailed feedback page
  const handleOpenTrialResult = () => {
    if (!trialExamId) return;

    if (trialExamFeedback) {
      try {
        localStorage.setItem(
          "combined_feedback_data",
          JSON.stringify(trialExamFeedback)
        );
        localStorage.setItem("combined_feedback_exam_id", trialExamId);
      } catch (e) {
        console.warn("Failed to store trial feedback in localStorage", e);
      }

      navigate(
        `/comprehensive-feedback-results?from=list&examId=${trialExamId}`,
        {
          state: { feedback: trialExamFeedback, examId: trialExamId },
        }
      );
    } else {
      const userId = getPaidUser()?.userId || "unknown_user";
      navigate(`/exam-details?examId=${trialExamId}&userId=${userId}`);
    }

    // Account-level "seen" flag
    markTrialBannerSeen();
  };

  const handleDismissTrialBanner = () => {
    markTrialBannerSeen();
  };

  const handleViewFeedback = (feedbackJson: any, examId: string) => {
    try {
      if (!feedbackJson) {
        console.warn("No feedback_json available for exam", examId);
        return;
      }

      localStorage.setItem(
        "combined_feedback_data",
        JSON.stringify(feedbackJson)
      );
      localStorage.setItem("combined_feedback_exam_id", examId);

      navigate(
        `/comprehensive-feedback-results?from=dashboard&examId=${examId}`,
        {
          state: { feedback: feedbackJson, examId },
        }
      );
    } catch (err) {
      console.error("Error storing feedback json for view:", err);
    }
  };

  // Load exams for dashboard
  useEffect(() => {
    const fetch = async () => {
      setLoadingExams(true);
      try {
        if (!isAuthenticated) return;
        const paidUser = getPaidUser();
        if (!paidUser) return;

        const res = await getExamListDashboard();
        console.log("exam data::::", res);

        const list = res?.exams || [];

        const formatted = list
          .filter((exam: any) => exam.feedback_json)
          .map((item: any) => {
            const getTaskCefr = (it: any, idx: number) => {
              try {
                if (it.feedback_json && Array.isArray(it.feedback_json.tasks)) {
                  const found = it.feedback_json.tasks.find(
                    (t: any) =>
                      Number(t.index) === idx || t.index === idx
                  );
                  if (found && found.cefr_level) return found.cefr_level;
                }
              } catch {
                // ignore
              }
              const legacy = it.tasks?.[String(idx)]?.[0]?.estimated_cefr;
              return legacy || "N/A";
            };

            return {
              date: item.created_at
                ? new Date(item.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "N/A",
              tasks: [
                `Task 1: ${getTaskCefr(item, 1)}`,
                `Task 2: ${getTaskCefr(item, 2)}`,
                `Task 3: ${getTaskCefr(item, 3)}`,
              ],
              gole_level: item.goal_level,
              level: item.global_cefr || "N/A",
              examId: item.id,
              feedback_json: item.feedback_json || null,
            };
          });

        setFetchedExams(formatted);

        // Only set previous goal if total exam count < 3 and no goal in localStorage
        if (list.length < 3) {
          const sortedExams = list.sort(
            (a: any, b: any) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()
          );
          const firstExam = sortedExams.find(
            (exam: any) => exam.feedback_json
          );
          if (firstExam && !localStorage.getItem("goal_level")) {
            setPreviousGoal(firstExam.goal_level);
            localStorage.setItem("goal_level", firstExam.goal_level);
            console.log(
              "Stored goal_level since exam count < 3:",
              firstExam.goal_level
            );
          } else if (localStorage.getItem("goal_level")) {
            console.log(
              "goal_level already exists in localStorage, skipping storage"
            );
          }
        } else {
          console.log("Exam count >= 3, skipping goal_level storage");
        }

        // Radar data from most recent exam of top 3 with feedback_json
        try {
          const topThree = (list || []).slice(0, 3);
          let foundFeedback: any = null;
          let examIdWithFeedback: string | null = null;

          for (const it of topThree) {
            if (it.feedback_json) {
              foundFeedback = it.feedback_json;
              examIdWithFeedback = it.id;
              break;
            }
          }

          if (examIdWithFeedback) {
            setLatestExamWithFeedbackId(examIdWithFeedback);
          }

          if (
            foundFeedback &&
            foundFeedback.radar &&
            foundFeedback.radar.scores
          ) {
            const s = foundFeedback.radar.scores;
            console.log("Found radar scores from feedback_json:", s);

            const radarArr = [
              { skill: "Fluency", value: s.fluency },
              { skill: "Grammar", value: s.grammar },
              { skill: "Vocabulary", value: s.vocabulary },
              { skill: "Pronunciation", value: s.pronunciation },
              { skill: "Comprehension", value: s.comprehension },
            ];
            setSkillData(radarArr);

            setRadarMeta({
              focus: (foundFeedback.radar.meta?.focus || []).filter(
                (item: string) => item && item.trim() !== ""
              ),
              strength: (foundFeedback.radar.meta?.strength || []).filter(
                (item: string) => item && item.trim() !== ""
              ),
            });

            if (
              foundFeedback.overall &&
              foundFeedback.overall.motivation_text
            ) {
              setMotivationText(foundFeedback.overall.motivation_text);
            }

            if (foundFeedback.overall && foundFeedback.overall.cefr_level) {
              setCurrentLevel(foundFeedback.overall.cefr_level);
            }
          }
        } catch (err) {
          console.warn("Failed to derive radar data from feedback_json", err);
        }
      } catch (err) {
        console.error("Failed to fetch exams for dashboard", err);
      } finally {
        setLoadingExams(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getPaidUser]);

  // Detect free trial exam
  useEffect(() => {
    const detectTrialExam = async () => {
      try {
        if (!isAuthenticated) return;
        const paidUser = getPaidUser();
        if (!paidUser) return;

        const response = await getExamList();
        const list = response?.exams || [];

        const trialExam = (list as any[]).find((item: any) => {
          const idStr = item?.id?.toString() || "";
          const sessionStr = item?.session_id?.toString() || "";
          return idStr.startsWith("guest_") || sessionStr.startsWith("guest_");
        });

        if (trialExam && trialExam.id) {
          setTrialExamId(trialExam.id);
          setTrialExamFeedback(trialExam.feedback_json || null);
          console.log("Dashboard: found trial exam for banner:", trialExam.id);
        } else {
          console.log("Dashboard: no trial exam found for this user");
        }
      } catch (err) {
        console.error("Dashboard: failed to detect trial exam:", err);
      }
    };

    detectTrialExam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getPaidUser]);

  // 🔍 Load has_seen_trial_banner (localStorage first, then Supabase)
  useEffect(() => {
    const fetchTrialBannerFlag = async () => {
      try {
        // 1️⃣ Local per-device fallback
        const localDismissed = localStorage.getItem(TRIAL_BANNER_KEY) === "true";
        if (localDismissed) {
          setHasSeenTrialBanner(true);
          return;
        }
  
        // 2️⃣ Account-level flag in Supabase
        if (!isAuthenticated) return;
        const paidUser = getPaidUser();
        if (!paidUser?.userId) return;
  
        const { data, error } = await supabase
          .from("users")
          .select("has_seen_trial_banner")
          .eq("id", paidUser.userId)
          .single();
  
        if (error) {
          console.error("Dashboard: failed to fetch has_seen_trial_banner", error);
          // Fallback: assume not seen so we can show it
          setHasSeenTrialBanner(false);
          return;
        }
  
        // data?.has_seen_trial_banner will be true/false or null
        setHasSeenTrialBanner(Boolean(data?.has_seen_trial_banner));
      } catch (err) {
        console.error("Dashboard: error fetching trial banner flag", err);
        setHasSeenTrialBanner(false);
      }
    };
  
    fetchTrialBannerFlag();
  }, [isAuthenticated, getPaidUser]);

  // Load subscriptions
  useEffect(() => {
    const fetchSubs = async () => {
      setSubsLoading(true);
      try {
        const res = await apiClient.post("/get_user_subscriptions", {});
        const data = res.data;
        const subs = Array.isArray(data.subscriptions)
          ? data.subscriptions
          : [];
        console.log("daata,", data);
        setUsername(data.subscriptions?.[0]?.username || "");
        setSubscriptions(subs);
      } catch (err) {
        console.warn("Failed to load subscriptions", err);
      } finally {
        setSubsLoading(false);
      }
    };

    fetchSubs();
  }, []);

  // Check subscription access
  useEffect(() => {
    const checkAccess = async () => {
      const result = await checkSubscriptionAccess();
      setSubscriptionActive(result.hasAccess);
      if (!result.hasAccess) {
        console.log(
          "❌ Dashboard: Subscription access denied -",
          result.reason
        );
      }
    };
    checkAccess();
  }, []);

  // Overall page loading
  useEffect(() => {
    if (!loadingExams && !subsLoading && !progressLoading) {
      setPageLoading(false);
    }
  }, [loadingExams, subsLoading, progressLoading]);

  const shouldShowTrialBanner =
    !!trialExamId && hasSeenTrialBanner === false;

  if (pageLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p
            className="text-muted-foreground mt-4"
            style={{ fontSize: "18px" }}
          >
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }
  const goalLabel =
  selectedGoal?.label ||
  localStorage.getItem("goal_level") ||
  "null";

    // 👇 ADD THIS RIGHT AFTER goalLabel
  const subscriptionCtaLabel = subscriptionActive ? "View My Plan" : "View Plans";

  return (
    <div className="dashboard-container">
      {/* --- Welcome header --- */}
      <div className="dashboard-welcome">
        {!loadingExams && fetchedExams.length > 0 ? (
          <div className="dashboard-welcome-inner">
            <div className="dashboard-welcome-text">
              <span>{username ? `Welcome, ${username}` : "Welcome"}</span>
              <p>Here's your progress toward {goalLabel}</p>
            </div>
            <div className="dashboard-goal">
              <span className="dashboard-goal-label">Goal level</span>
              <div className="goal-select-wrapper">
                <Select
                  value={
                    fetchedExams.length > 0
                      ? selectedGoal
                        ? selectedGoal
                        : goalOptions.find(
                            (opt) =>
                              opt.value ===
                              (localStorage.getItem("goal_level") ||
                                previousGoal)
                          ) || null
                      : null
                  }
                  onChange={handleChangeGole}
                  options={goalOptions}
                  isSearchable={false}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      width: isMobile ? "100%" : "119px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      height: "40px",
                      padding: "0 12px",
                      borderRadius: "10px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                      boxShadow: "none",
                      backgroundColor: "#fff",
                      opacity: 1,
                      transition: "all 100ms",
                      fontSize: "14px",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#2563eb",
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0",
                      margin: "0",
                      width: "100%",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#111827",
                      fontSize: "14px",
                      fontWeight: "500",
                      margin: "0",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      paddingRight: "0px",
                    }),
                    dropdownIndicator: (base, state) => ({
                      ...base,
                      transition: "transform 0.2s ease",
                      transform: state.selectProps.menuIsOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      padding: "0",
                      marginLeft: "6px",
                    }),
                    option: (base, { isFocused }) => ({
                      ...base,
                      backgroundColor: isFocused ? "#f3f4f6" : "white",
                      color: "#111827",
                      cursor: "pointer",
                    }),
                  }}
                />
              </div>

              <ConfirmationModal
                open={showConfirmationModal}
                onOpenChange={setShowConfirmationModal}
                title={`Change Goal?`}
                description="Updating your goal level changes how future feedback and progress are calculated.
Your past exams stay exactly the same. Only your goal level is updated.
                "
                confirmLabel="Update Goal"
                cancelLabel="Cancel"
                onConfirm={handleConfirmGoal}
                onCancel={handleCancelGoal}
                isLoading={isConfirming}
              />
            </div>
          </div>
        ) : (
          <>
            {!loadingExams && fetchedExams.length > 0 ? (
              <div className="dashboard-welcome-inner-no-data">
                <div className="dashboard-welcome-text text-center">
                  <span>{username ? `Welcome, ${username}` : "Welcome"}</span>
                  <p>Here's your progress toward {selectedGoal?.label || "B2"}</p>
                </div>
              </div>
            ) : (
              <div className="dashboard-welcome-inner-no-data text-center">
                <div className="dashboard-welcome-text-no-data">
                  <span>{username ? `Welcome, ${username}` : "Welcome"}</span>
                  <p>Ready to start your first exam?</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Trial banner --- */}
      {shouldShowTrialBanner && (
        <div className="dashboard-trial-banner">
          <div className="dashboard-trial-banner-content">
            <p className="dashboard-trial-banner-title">
              We’ve saved your free trial speaking result.
            </p>
            <p className="dashboard-trial-banner-text">
              You can review it now, or find it anytime in your Exam History
              (top-right menu).
            </p>
          </div>
          <div className="dashboard-trial-banner-actions">
            <Button
              className="trial-banner-button h-9 px-4 text-[14px] font-medium rounded-[999px] bg-[#005FCC] hover:bg-[#0049A8]"
              onClick={handleOpenTrialResult}
            >
              View My Free Trial Result
            </Button>
            <button
              type="button"
              className="trial-banner-dismiss text-[13px]"
              onClick={handleDismissTrialBanner}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* --- Main content --- */}
      <div className="dashboard-main-content">
        {/* Left Column */}
        <div className="dashboard-left-column">
          {/* Skill breakdown */}
          {!loadingExams && fetchedExams.length > 0 ? (
            <div className="dashboard-radar-card-inner">
              <div className="dashboard-radar-card-header">
                <span>Skill breakdown</span>
                <p>Your latest exam performance</p>
              </div>

              <div className="skill-breakdown-chart">
                <ResponsiveContainer className="chart-container">
                  <RadarChart data={skillData}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarRadiusAxis
                      domain={[0, 100]}
                      angle={0}
                      tickCount={5}
                      tick={false}
                      axisLine={false}
                    />
                    <PolarAngleAxis
                      dataKey="skill"
                      tick={({ payload, x, y, cx, cy }) => {
                        const mobile = window.innerWidth < 768;

                        const numX = Number(x);
                        const numY = Number(y);
                        const numCX = Number(cx);
                        const numCY = Number(cy);

                        const labelOffset = 8; 

                        const dx = numX - numCX;
                        const dy = numY - numCY;
                        const len = Math.sqrt(dx * dx + dy * dy);

                        let newX =
                          numCX + (dx / len) * (len + labelOffset);
                        let newY =
                          numCY + (dy / len) * (len + labelOffset);

                        if (!mobile && payload.value === "Comprehension") {
                          newX -= 42;
                          newY += -2;
                        }
                        if (!mobile && payload.value === "Grammar") {
                          newX -= -22;
                          newY += -2;
                        }
                        if (!mobile && payload.value === "Pronunciation") {
                          newX -= 35;
                          newY += 0;
                        }
                        if (!mobile && payload.value === "Vocabulary") {
                          newX -= -30;
                          newY += 0;
                        }

                        if (!mobile) {
                          return (
                            <text
                              x={newX}
                              y={newY}
                              dy={4}
                              textAnchor="middle"
                              fill="#6B7280"
                              fontFamily="Inter, sans-serif"
                              fontWeight={400}
                              fontSize={13}
                              style={{
                                lineHeight: "100%",
                                fontSize: "13px",
                                fontWeight: 400,
                                color: "#6B7280",
                                letterSpacing: "0%",
                              }}
                            >
                              {payload.value}
                            </text>
                          );
                        }

                        // ✅ MOBILE: keep labels horizontal, outside chart, and prevent overlap
                        const displayLabel =
                          payload.value === "Comprehension" ? "Compr." : payload.value;
                        
                        // ✅ Anchor text so it grows AWAY from the chart (prevents going inside)
                        let textAnchor: "start" | "middle" | "end" = "middle";
                        if (payload.value === "Grammar" || payload.value === "Vocabulary") {
                          textAnchor = "start"; // right side labels extend to the right
                        }
                        if (payload.value === "Comprehension" || payload.value === "Pronunciation") {
                          textAnchor = "end"; // left side labels extend to the left
                        }
                        
                        // Optional small vertical nudges (keeps spacing looking like desktop)
                        let adjustedY = newY;
                        if (payload.value === "Fluency") adjustedY -= 2;
                        if (payload.value === "Vocabulary" || payload.value === "Pronunciation")
                          adjustedY += 6;
                        
                        return (
                          <text
                            x={newX}
                            y={adjustedY}
                            dy={4}
                            textAnchor={textAnchor}
                            fill="#6B7280"
                            fontFamily="Inter, sans-serif"
                            fontWeight={400}
                            fontSize={14} // ✅ slightly bigger on mobile now that "Compr." frees space
                            style={{
                              lineHeight: "100%",
                              letterSpacing: "0%",
                            }}
                          >
                            {displayLabel}
                          </text>
                        );
                      }}
                    />
                    <Radar
                      dataKey="value"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.3}
                      isAnimationActive={true}
                      animationDuration={400}
                      animationEasing="ease-out"
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="skill-breakdown-meta">
                <div className="motivation-text-container">
                  <p className="motivation-small">
                    {motivationText.split(".")[0] + "."}
                  </p>
                  <p className="motivation-rest">
                    {motivationText
                      .split(".")
                      .slice(1)
                      .join(".")
                      .trim()}
                  </p>
                </div>
                <div className="dashboard-text">
                  {radarMeta?.strength?.length > 0 && (
                    <p>
                      <span>Strength: </span>
                      {radarMeta.strength
                        .map(capitalizeFirstLetter)
                        .join(" and ")}
                    </p>
                  )}

                  {radarMeta?.strength?.length > 0 &&
                    radarMeta?.focus?.length > 0 && (
                      <span className="dot">•</span>
                    )}

                  {radarMeta?.focus?.length > 0 && (
                    <p>
                      <span>Focus: </span>
                      {radarMeta.focus
                        .map(capitalizeFirstLetter)
                        .join(" and ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="dashboard-card-inner-no-data">
              <div className="dashboard-logo-no-data">
                <img src={radar} alt="" />
              </div>
              <div className="dashboard-radar-text-no-data">
                <span>Your latest exam performance</span>
                <p>
                  You’ll see your first results here after completing your
                  first full exam.
                </p>
              </div>
            </div>
          )}

          {/* Progress section */}
          {!loadingExams && fetchedExams.length > 2 ? (
            progressLoading ? (
              <div className="dashboard-progress-card">
                <div className="dashboard-progress-card-inner">
                  <div className="progress-header">
                    <div className="progres-status">
                      <div className="progres-status-text">
                        Loading Progress...
                      </div>
                    </div>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mt-4"></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="dashboard-progress-card">
                <div className="dashboard-progress-card-inner">
                  <div className="progress-header">
                    <div className="progres-status">
                      <div className="progres-status-text dashboard-card-title">
                        {progressData?.message.title || "Overall Progress"}
                      </div>
                      <div className="progres-status-per">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{progressData?.percent ?? 0}%</span>
                          {isImproving && (
                            <span style={{ color: '#22c55e', fontSize: '16px', lineHeight: '1' }}>↗</span>
                          )}
                          </div>
                        </div>
                      </div>
                    <Progress
                      value={progressData?.percent ?? 0}
                      className={`progress-bar ${(progressData?.percent ?? 0) >= 90 ? 'progress-bar-goal-close' : ''}`}
                    />
                  </div>
                  <div className="progress-tip">
                    <span>{progressData?.message.text || ""}</span>
                  </div>
                  <div className="progress-levels">
                    <div className="progress-current">
                      <div className="progress-current-level">
                        {progressData?.current_level || currentLevel}
                      </div>
                      <div className="progress-current-label">Current</div>
                    </div>
                    <div className="progress-divider" />
                    <div className="progress-goal">
                      <div className="progress-goal-level">
                        {selectedGoal?.label || "B2"}
                      </div>
                      <div className="progress-current-label">Goal</div>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="dashboard-card-inner-no-data">
              <div className="dashboard-logo-no-data">
                <img src={progress} alt="" />
              </div>
              <div className="dashboard-radar-text-no-data">
                <span>Your progress chart</span>
                <p>Complete 3 full exams to unlock your progress chart.</p>
              </div>
            </div>
          )}

          {/* Exam History - Desktop Only */}
          {!loadingExams && fetchedExams.length > 0 && !isMobile && (
            <div className="dashboard-exam-history-card">
              <div className="dashboard-exam-header">
                <h1 className="dashboard-card-title-exam">Exam history</h1>
                <p className="dashboard-card-subtitle">
                  Your full exam attempts
                </p>
              </div>

              <div className="exam-history-list">
                {loadingExams ? (
                  <div className="exam-history-loading">
                    <Clock className="exam-history-loading-icon" /> Loading
                    exams...
                  </div>
                ) : fetchedExams.length === 0 ? (
                  <div className="exam-history-empty">
                    No exam history found.
                  </div>
                ) : (
                  fetchedExams.slice(0, 3).map((exam, index) => (
                    <div key={index} className="exam-history-item">
                      <div className="exam-history-content">
                        <div className="exam-history-details">
                          <div className="exam-history-date">
                            {exam.date}
                          </div>

                          <div className="exam-history-tasks">
                            {exam.tasks.map(
                              (task: string, idx: number) => (
                                <span key={idx} className="task-item">
                                  {task}
                                </span>
                              )
                            )}

                            {isMobile && (
                              <div className="exam-history-badge">
                                <span>
                                  {exam.feedback_json.overall.cefr_level}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="exam-history-actions">
                          {!isMobile && (
                            <div className="exam-history-badge">
                              <span>
                                {exam.feedback_json.overall.cefr_level}
                              </span>
                            </div>
                          )}
                          <div className="exam-history-button-view">
                            {exam.feedback_json ? (
                              <button
                                onClick={() =>
                                  handleViewFeedback(
                                    exam.feedback_json,
                                    exam.examId
                                  )
                                }
                              >
                                <span>View Feedback</span>
                              </button>
                            ) : (
                              <button
                                className="exam-history-button-no"
                                disabled
                              >
                                No Feedback
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="dashboard-right-column">
          {/* Actions */}
          <div className="dashboard-action-cards">
            <div className="dashboard-action-header">
              <span>Actions</span>
              {!loadingExams && fetchedExams.length > 0 ? (
                <p>Choose your next step</p>
              ) : (
                <p>Take your first exam to unlock task practice.</p>
              )}
            </div>

            {isTask2MaintenanceEnabled() ? (
              <Task2MaintenanceNotice variant="fullExam" className="mb-4" />
            ) : null}

            {!loadingExams && fetchedExams.length > 0 ? (
              <div className="actions-buttons">
                <button
                  className={`actions-button ${
                    !subscriptionActive ? "expired-cta" : ""
                  }`}
                  onClick={() => {
                    if (isTask2MaintenanceEnabled()) {
                      return;
                    }
                    if (subscriptionActive) {
                      if (fetchedExams.length === 0) {
                        navigate("/level-option");
                      } else {
                        navigate("/mic-test", { state: { examMode: "full" } });
                      }
                    } else {
                      navigate("/subscription");
                    }
                  }}
                  disabled={isTask2MaintenanceEnabled()}
                  style={{
                    opacity: isTask2MaintenanceEnabled() ? 0.6 : 1,
                    cursor: isTask2MaintenanceEnabled() ? "not-allowed" : "pointer",
                  }}
                  title={
                    isTask2MaintenanceEnabled()
                      ? "Full speaking exam is temporarily unavailable"
                      : !subscriptionActive
                      ? "Please renew your subscription to access the exam"
                      : ""
                  }
                >
                  <img src={play} alt="play" />
                  {!subscriptionActive
                    ? "Renew Access"
                    : isTask2MaintenanceEnabled()
                    ? "Temporarily unavailable"
                    : "Start New Exam"}
                </button>

                <button
                  className="actions-button-outline"
                  onClick={() =>
                    navigate("/templates", {
                      state: {
                        examId: latestExamWithFeedbackId,
                        selectedTask: selectedTaskType,
                      },
                    })
                  }
                  disabled={!latestExamWithFeedbackId}
                >
                  <img src={refresh} alt="refresh" />
                  Practice a Task
                </button>
              </div>
            ) : (
              <div className="actions-buttons">
                <button
                  className={`actions-button ${
                    !subscriptionActive ? "expired-cta" : ""
                  }`}
                  onClick={() => {
                    if (isTask2MaintenanceEnabled()) {
                      return;
                    }
                    if (subscriptionActive) {
                      navigate("/level-option");
                    } else {
                      navigate("/subscription");
                    }
                  }}
                  disabled={isTask2MaintenanceEnabled()}
                  style={{
                    opacity: isTask2MaintenanceEnabled() ? 0.6 : 1,
                    cursor: isTask2MaintenanceEnabled() ? "not-allowed" : "pointer",
                  }}
                  title={
                    isTask2MaintenanceEnabled()
                      ? "Full speaking exam is temporarily unavailable"
                      : !subscriptionActive
                      ? "Please renew your subscription to access the exam"
                      : ""
                  }
                >
                  <img src={play} alt="play" />
                  {!subscriptionActive
                    ? "Renew Access"
                    : isTask2MaintenanceEnabled()
                    ? "Temporarily unavailable"
                    : "Take Your First Exam"}
                </button>
                <button
                  className="actions-button"
                  style={{
                    backgroundColor: "white",
                    color: "#1D232A",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }}
                  disabled
                >
                  <img src={tem} alt="refresh" />
                  Practice Tasks
                </button>
              </div>
            )}
          </div>

          {/* Tips & Examples */}
          <div className="dashboard-action-cards">
            <div className="dashboard-action-header">
              <span>Learn with tips and examples</span>
              <p>Study model answers and task structures before practicing.</p>
            </div>

            <div className="templates-select-container">
              <button
                className="actions-button-outline"
                style={{
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate("/templates", {
                    state: {
                      showTips: true,
                      selectedTask: null,
                      disableTopic: fetchedExams.length === 0,
                    },
                  })
                }
              >
                <img src={tem} alt="refresh" />
                Explore Tips and Examples
              </button>
            </div>
          </div>

          {/* Exam History - Mobile Only */}
          {fetchedExams.length > 0 && isMobile && (
            <div className="dashboard-exam-history-card">
              <div className="dashboard-exam-header">
                <h1 className="dashboard-card-title-exam">Exam history</h1>
                <p className="dashboard-card-subtitle">
                  Your full exam attempts
                </p>
              </div>

              <div className="exam-history-list">
                {fetchedExams.slice(0, 3).map((exam, index) => (
                  <div key={index} className="exam-history-item">
                    <div className="exam-history-content">
                      <div className="exam-history-details">
                        <div className="exam-history-date">
                          {exam.date}
                        </div>

                        <div className="exam-history-tasks">
                          {exam.tasks.map(
                            (task: string, idx: number) => (
                              <span key={idx} className="task-item">
                                {task}
                              </span>
                            )
                          )}

                          {isMobile && (
                            <div className="exam-history-badge">
                              <span>
                                {exam.feedback_json.overall.cefr_level}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="exam-history-actions">
                        <div className="exam-history-button-view">
                          {exam.feedback_json ? (
                            <button
                              onClick={() =>
                                handleViewFeedback(
                                  exam.feedback_json,
                                  exam.examId
                                )
                              }
                            >
                              <span>View Feedback</span>
                            </button>
                          ) : (
                            <button
                              className="exam-history-button-no"
                              disabled
                            >
                              No Feedback
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscription */}
          <div className="dashboard-action-cards">
            <div className="dashboard-action-header">
              <span>Subscription</span>
              <p>Manage your plan</p>
            </div>

            <div className="subscription-plan">
              <div className="subscription-details">
                {subsLoading ? (
                  <div className="text-sm text-muted-foreground">
                    Loading subscription...
                  </div>
                ) : subscriptions.length === 0 ? (
                  <>
                    <div className="subscription-status">
                      <p>
                        Plan:{" "}
                        <span style={{ color: "#EF4444" }}>Free</span>
                      </p>
                      <div className="sub-status-inner">
                        <img src={watch} alt="watch" />
                        <span style={{ color: "#EF4444" }}>Expired</span>
                      </div>
                    </div>
                    <div>
                      <img src={pre} alt="pre" />
                    </div>
                  </>
                ) : (
                  (() => {
                    const active =
                      subscriptions.find(
                        (s: any) =>
                          s.status &&
                          s.status.toLowerCase() === "active"
                      ) || subscriptions[0];

                    const planName = active?.plan_name || "Pro";

                    let daysRemaining: React.ReactNode = "—";
                    if (active?.end_date) {
                      const diff = Math.ceil(
                        (new Date(active.end_date).getTime() -
                          Date.now()) /
                          (1000 * 60 * 60 * 24)
                      );
                      daysRemaining =
                        diff > 0 ? (
                          `${diff} ${diff === 1 ? "day" : "days"} remaining`
                        ) : (
                          <span style={{ color: "#EF4444" }}>Expired</span>
                        );
                    }

                    return (
                      <>
                        <div className="subscription-status">
                          <p>
                            Plan:{" "}
                            <span
                              style={{
                                color: subscriptionActive
                                  ? undefined
                                  : "#EF4444",
                              }}
                            >
                              {planName}
                            </span>
                          </p>
                          <div className="sub-status-inner">
                            <img src={watch} alt="watch" />
                            <span>{daysRemaining}</span>
                          </div>
                        </div>
                        <div>
                          <img src={pre} alt="pre" />
                        </div>
                      </>
                    );
                  })()
                )}
              </div>

              <div>
                <Button
                  className="subscription-button h-12 px-4 text-[14px] font-medium rounded-[10px]"
                  onClick={() => navigate("/subscription")}
                >
                  <img src={expendPlan} alt="" />
                  {subscriptionCtaLabel}
                </Button>
              </div>

              <div>
                <p className="subscription-footer">
                  Gardez l&apos;accès à vos progrès sans interruption
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


